/**
 * AI Recruit - Header Component
 * Top navigation bar with user menu and notifications
 */

import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { RootState } from '@/stores'
import { toggleSidebar } from '@/stores/ui/uiSlice'
import { useAuth } from '@/hooks/useAuth'
import { usePermission } from '@/hooks/usePermission'
import { ORG_SETTINGS_CAPABILITIES } from '@/config/permissions'
import { formatToLocalTime } from '../../utils/dateUtils'
// CHANGE 1: Import your custom hook
import { useClickOutside } from '@/hooks/useClickOutside'
import {
  Menu,
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Key,
  Building2,
} from 'lucide-react'
import clsx from 'clsx'
import ChangePasswordModal from '@/components/common/ChangePasswordModal'

const Header: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const { notifications } = useSelector((state: RootState) => state.ui)
  const { logout } = useAuth()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false)
  const { can } = usePermission()
  // §25 — mirrors /org-settings' own route guard exactly (the OR of its 6
  // real sub-capabilities), not a separate `orgSettings.access` capability
  // (removed — it had no purpose independent of these six).
  const canAccessOrgSettings = ORG_SETTINGS_CAPABILITIES.some((c) => can(c))

  // CHANGE 2: Call the hook and tell it how to close the menu
  const userMenuRef = useClickOutside<HTMLDivElement>(() => {
    setUserMenuOpen(false);
  });

  const unreadNotifications = notifications.filter(n => !n.read).length

  const handleLogout = async () => {
    await logout()
    setUserMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
          >
            <Menu className="w-5 h-5 text-gray-500" />
          </button>

          {/* Desktop menu button */}
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="hidden lg:block p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications ... */}
          <div className="relative">
            {/* ... your commented out notifications code ... */}
          </div>

          {/* User menu */}
          {/* CHANGE 3: Attach the ref to the dropdown's container div */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                {user?.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.fullName}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <User className="w-4 h-4 text-primary-600" />
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900">
                  {user?.fullName}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.role}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {/* User dropdown */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.email}
                  </p>
                </div>
                <div className="py-2">
                  {canAccessOrgSettings && (
                    <button
                      onClick={() => {
                        navigate('/org-settings')
                        setUserMenuOpen(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Building2 className="w-4 h-4 mr-3 flex-shrink-0" />
                      Organization Settings
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setChangePasswordModalOpen(true)
                      setUserMenuOpen(false)
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Key className="w-4 h-4 mr-3 flex-shrink-0" />
                    Reset Password
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                  >
                    <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search ... */}
      <div className="md:hidden px-4 pb-4">
       {/* ... your mobile search code ... */}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
      />
    </header>
  )
}

export default Header